import React, {
  createRef,
  memo,
  useLayoutEffect,
  useState,
  useEffect,
  Children,
  useMemo,
  ChangeEvent,
  useRef,
} from 'react'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { useDebounce } from 'use-debounce'
import UUID from 'uuid/v4'
import { useDropdownPosition } from 'utils/dropdownPosition'
import AutoCompleteListBox from './AutoCompleteListBox'
import AutoCompleteTrigger from './AutoCompleteTrigger'
import { IOption, IOptionGroup, ISuggestionOption } from './types'
import { isOptionGroup } from './utils'
import 'scss/5_components/_dpl-autocomplete.scss'

export const createOption = (id: string, text: string, params?: Partial<IOption>): IOption => ({
  id: `${id}`,
  text,
  ...params,
})

export const createOptionGroup = (groupName: string, options: IOption[]): IOptionGroup => ({
  groupName,
  options,
})

export const createAddOption = (id: string, text: string, params: Partial<IOption> = {}): IOption =>
  createOption(id, text, {
    className: 'add-option',
    isAddOption: true,
    ...params,
  })

export interface IValue {
  value: string
  id?: string
}

const getHighlightedMarkup = (suggestion: string, value: string): HTMLElement => {
  const matches = match(suggestion, value)
  const parts = parse(suggestion, matches)

  return ((
    <span>
      {Children.map(
        parts.map(part => <span className={part.highlight ? 'b' : ''}>{part.text}</span>),
        entry => entry
      )}
    </span>
  ) as unknown) as HTMLElement
}

export const createOptionWithMarkup = (
  value: string,
  { className, id, modelType, text }: IOption
): IOption => ({
  id,
  text,
  markup: getHighlightedMarkup(text, value),
  className,
  modelType,
})

export const createOptionGroupWithMarkup = (
  value: string,
  { groupName, options }: IOptionGroup
): IOptionGroup => ({
  groupName,
  options: options.map(({ className, id, modelType, text }) =>
    createOptionWithMarkup(value, { id, text, className, modelType })
  ),
})

const withQuery = (
  suggestions: ISuggestionOption[],
  addOption: IOption,
  query?: string
): ISuggestionOption[] => {
  const newAddOption = createAddOption(`${addOption.id}`, addOption.text)
  return query ? [...suggestions, newAddOption] : [newAddOption, ...suggestions]
}

const additionalOptions = (
  suggestions: ISuggestionOption[],
  addOptionsArr: IOption[],
  query?: string
) => (query ? [...suggestions, ...addOptionsArr] : [...addOptionsArr, ...suggestions])

// highlight query in / from markup
const addMarkupToSuggestions = (
  value: string,
  suggestions: ISuggestionOption[],
  addOption?: IOption,
  addOptionsArr?: IOption[],
  query?: string
): ISuggestionOption[] => {
  if (addOptionsArr) {
    suggestions = addOptionsArr ? additionalOptions(suggestions, addOptionsArr, query) : suggestions
  } else {
    suggestions = addOption ? withQuery(suggestions, addOption, query) : suggestions
  }
  return suggestions.map((suggestion: ISuggestionOption) => {
    if (isOptionGroup(suggestion)) {
      return createOptionGroupWithMarkup(value, suggestion)
    }
    return createOptionWithMarkup(value, suggestion)
  })
}

const moveBetweenOptions = (
  optionsList: NodeListOf<HTMLElement>,
  direction: 'up' | 'down',
  activeElement: Element
): void => {
  const activeElementIndex: number = Array.from(optionsList).indexOf(activeElement as HTMLElement)
  const optionsListLength: number = optionsList.length

  if (activeElementIndex !== -1) {
    activeElement.setAttribute('tabindex', '-1')

    let nextElementIndex: number

    if (direction === 'up') {
      if (activeElementIndex === 0) {
        nextElementIndex = optionsListLength - 1
      } else {
        nextElementIndex = activeElementIndex - 1
      }
    } else if (activeElementIndex === optionsListLength - 1) {
      nextElementIndex = 0
    } else {
      nextElementIndex = activeElementIndex + 1
    }

    optionsList[nextElementIndex].setAttribute('tabindex', '0')
    optionsList[nextElementIndex].focus()
  }
}

const focusInAutoCompleteRegion = (listBox: HTMLElement): boolean => {
  const { activeElement } = document
  const options: HTMLElement[] = Array.from(listBox.querySelectorAll('[role="option"]'))
  const focusableElements = [listBox, ...options]
  const showListBox = focusableElements.includes(activeElement as HTMLElement)
  return showListBox
}

const clickInAutoCompleteRegion = (event: MouseEvent, listBox: HTMLElement): boolean => {
  const { height: listBoxHeight } = listBox.getBoundingClientRect()
  const {
    height: autoCompleteHeight,
    left: xMin,
    top: yMin, // using top instead of y because of IE
    width: autoCompleteWidth, // using left instead of x because of IE
  } = (listBox.parentElement as Element).getBoundingClientRect()

  const xMax = xMin + autoCompleteWidth
  const yMax = yMin + autoCompleteHeight + listBoxHeight

  const xWithinRange = xMin <= event.clientX && event.clientX <= xMax
  const yWithinRange = yMin <= event.clientY && event.clientY <= yMax

  return xWithinRange && yWithinRange
}

interface IAutoCompleteProps {
  dataTest?: string
  disabled?: boolean
  error?: string
  id: string
  isLoading: boolean
  label: string
  onChange?: (selection: IValue) => void
  onOptionSelection: (option: IOption) => void
  placeholder?: string
  required?: boolean
  suggestions: ISuggestionOption[]
  type?: 'location'
  value?: string
  large?: boolean
  addOption?: IOption
  addOptionsArr?: IOption[]
  onAddOptionSelected?: () => void
  onFocus?: (triggerInputValue: string) => void
  onBlur?: (e: React.FocusEvent<Element>) => void
  clearable?: boolean
  ariaLabel?: string
}

/** @deprecated Create an abstraction of <AutocompleteBase /> in Transfix UI instead */
const AutoComplete = memo<IAutoCompleteProps>(
  ({
    addOption,
    addOptionsArr,
    ariaLabel,
    clearable,
    dataTest,
    disabled,
    error,
    isLoading,
    label,
    large,
    onAddOptionSelected,
    onBlur,
    onChange,
    onFocus,
    onOptionSelection,
    placeholder = '',
    required,
    suggestions,
    type,
    value = '',
  }) => {
    const [query] = useDebounce(value, 500)
    const triggerRef = createRef<HTMLInputElement>()
    const listBoxRef = createRef<HTMLDivElement>()

    const [triggerInputValue, setTriggerInputValue] = useState<string>('')
    const [previousTriggerInputValue, setPreviousTriggerInputValue] = useState<string>('')
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const close = () => setIsOpen(false)
    const [, style] = useDropdownPosition(isOpen, close, triggerRef, true)

    /**
     * Returns a flattened array of all suggestions
     * for find/filter methods
     */
    const flattenedSuggestionsArray: IOption[] = useMemo(
      () =>
        suggestions
          .map((option: ISuggestionOption) => {
            if (isOptionGroup(option)) {
              return option.options
            }

            return option
          })
          .flat(),
      [suggestions]
    )

    useEffect(() => setTriggerInputValue(value), [value])

    const triggerFocusHandler = () => {
      if (onFocus) {
        onFocus(triggerInputValue)
      }
      setPreviousTriggerInputValue(triggerInputValue)
      setTriggerInputValue('')
      setIsOpen(true)
    }

    const triggerBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const isRelatedTargetInListBox = Array.prototype.includes.call(
        listBoxRef.current?.children,
        e.relatedTarget
      )

      if (!triggerInputValue && !!previousTriggerInputValue && !isRelatedTargetInListBox) {
        setTriggerInputValue(previousTriggerInputValue)
        setPreviousTriggerInputValue('')
        setIsOpen(false)

        if (onBlur) {
          onBlur(e)
        }
      }
    }

    const triggerValueChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value: newInputValue },
      } = e
      setTriggerInputValue(newInputValue)
      if (onChange) {
        onChange({ value: newInputValue })
      }
    }

    const triggerClearButtonHandler = () => setPreviousTriggerInputValue('')

    const optionSelectHandler = (option: IOption): void => {
      setTriggerInputValue(option.text)
      onOptionSelection(option)
      setIsOpen(false)
      if (addOption && onAddOptionSelected && option.id === addOption?.id) {
        onAddOptionSelected()
      }
    }

    const triggerEventsHandler = (event: KeyboardEvent): void => {
      event.stopPropagation()

      const listBox = listBoxRef.current

      const optionsList: NodeListOf<HTMLElement> = listBox
        ? listBox.querySelectorAll('[role="option"]')
        : (([] as unknown) as NodeListOf<HTMLElement>)
      const optionsListLength: number = optionsList.length

      switch (event.key) {
        case 'Escape':
          /*
          Clears the textbox
          If the listbox is displayed, closes it.
        */
          triggerValueChangeHandler({
            target: { value: '', id: triggerRef?.current?.id },
          } as ChangeEvent<HTMLInputElement>)
          setIsOpen(false)
          break
        case 'Up':
        case 'ArrowUp':
          /*
          If the listbox is displayed, moves focus to the last suggested value.
        */
          // prevents scrolling default behaviour
          event.preventDefault()
          if (optionsListLength) {
            optionsList[optionsListLength - 1].setAttribute('tabindex', '0')
            optionsList[optionsListLength - 1].focus()
          }
          break
        case 'Down':
        case 'ArrowDown':
          /*
          Moves focus to the first suggested value.
        */
          // prevents scrolling default behaviour
          event.preventDefault()
          if (optionsListLength) {
            optionsList[0].setAttribute('tabindex', '0')
            optionsList[0].focus()
          }
          break
        default:
          /*
          Keys used for cursor movement and text manipulation, such as Delete and Shift + Right Arrow.
          An HTML input with type="text" is used for the textbox so the browser will provide platform-specific editing keys.
        */
          setIsOpen(!!value)
          break
      }
    }

    const listBoxEventsHandler = (event: KeyboardEvent): void => {
      event.preventDefault()
      event.stopPropagation()
      const listBox = listBoxRef.current

      const optionsList: NodeListOf<HTMLElement> = listBox
        ? listBox.querySelectorAll('[role="option"]')
        : (([] as unknown) as NodeListOf<HTMLElement>)

      switch (event.key) {
        case 'Tab':
        case 'Enter':
          /*
          Sets the textbox value to the content of the focused option in the listbox.
          Closes the listbox.
          Sets focus on the textbox.
        */
          if (document.activeElement) {
            const { activeElement } = document
            const activeElementIndex = Array.from(optionsList).indexOf(activeElement as HTMLElement)

            if (activeElementIndex !== -1) {
              const optionText = activeElement.getAttribute('aria-label') as string
              if (optionText) {
                const { id, modelType } =
                  flattenedSuggestionsArray.find(s => s.text === optionText) || {}
                optionSelectHandler({ text: optionText, id, modelType })
              }
            }

            if (triggerRef.current) {
              triggerRef.current.focus()
              setIsOpen(false)
            }
          }
          break
        case 'Escape':
          /*
          Closes the listbox.
          Sets focus on the textbox.
          Clears the textbox.
        */

          if (triggerRef.current) {
            triggerRef.current.focus()
            if (onChange) {
              triggerValueChangeHandler({
                target: { value: '', id: triggerRef?.current?.id },
              } as ChangeEvent<HTMLInputElement>)
            }
            setIsOpen(false)
          }
          break
        case 'Down':
        case 'ArrowDown':
          /*
          Moves focus to the next option.
          If focus is on the last option, moves focus to the first option.
          Note: This wrapping behavior is useful when Home and End move the editing cursor as described below.
        */
          moveBetweenOptions(optionsList, 'down', document.activeElement as Element)
          break
        case 'Up':
        case 'ArrowUp':
          /*
          Moves focus to the previous option.
          If focus is on the first option, moves focus to the last option.
          Note: This wrapping behavior is useful when Home and End move the editing cursor as described below.
        */
          moveBetweenOptions(optionsList, 'up', document.activeElement as Element)
          break
        default:
          /*
          Moves focus to the textbox.
          Types the character in the textbox.
        */
          if (triggerRef.current) {
            triggerRef.current.focus()
            setIsOpen(false)
          }
          break
      }
    }

    const documentFocusHandler = (event: FocusEvent): void => {
      event.preventDefault()
      event.stopPropagation()

      if (listBoxRef.current && triggerRef.current) {
        const shouldShowListBox: boolean = focusInAutoCompleteRegion(listBoxRef.current)
        setIsOpen(shouldShowListBox)
      }
    }

    const documentClickHandler = (event: MouseEvent): void => {
      // when click event doesn't occur within co-ordinates of textBox or
      // listBox, close the listBox
      if (listBoxRef.current) {
        const shouldShowListBox: boolean = clickInAutoCompleteRegion(event, listBoxRef.current)
        if (
          triggerRef.current &&
          event.target &&
          triggerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(shouldShowListBox)
        }
      }
    }

    useLayoutEffect(() => {
      const triggerRefNode = triggerRef.current
      const listboxRefNode = listBoxRef.current
      if (triggerRefNode && listboxRefNode) {
        triggerRefNode.addEventListener('keydown', triggerEventsHandler, true)
        listboxRefNode.addEventListener('keydown', listBoxEventsHandler, true)
        document.addEventListener('focus', documentFocusHandler, true)
        document.addEventListener('click', documentClickHandler, true)
      }
      return () => {
        if (triggerRefNode) {
          triggerRefNode.removeEventListener('keydown', triggerEventsHandler, true)
        }
        if (listboxRefNode) {
          listboxRefNode.removeEventListener('keydown', listBoxEventsHandler, true)
        }
        document.removeEventListener('click', documentClickHandler, true)
      }
    })

    // set ids for listbox and trigger
    // these are refs because they should not re-render
    const listBoxIdRef = useRef(`autocomplete-listbox-${UUID()}`)
    const textBoxIdRef = useRef(`autocomplete-textbox-${UUID()}`)
    const listBoxID = listBoxIdRef?.current
    const textBoxID = textBoxIdRef?.current

    const options: ISuggestionOption[] = addMarkupToSuggestions(
      value,
      suggestions,
      addOption,
      addOptionsArr,
      query
    ) as ISuggestionOption[]

    return (
      <div
        role='combobox'
        className='dpl-autocomplete'
        aria-haspopup='listbox'
        aria-owns={listBoxID}
        aria-controls={listBoxID}
        aria-expanded={isOpen && options.length > 0}>
        <AutoCompleteTrigger
          dataTest={dataTest}
          disabled={disabled}
          error={!isOpen && error ? error : ''}
          label={label}
          listbox={listBoxID}
          value={triggerInputValue}
          onChange={triggerValueChangeHandler}
          onBlur={triggerBlurHandler}
          onFocus={triggerFocusHandler}
          onClearButtonClick={triggerClearButtonHandler}
          placeholder={previousTriggerInputValue || placeholder}
          ref={triggerRef}
          required={required}
          textBoxID={textBoxID}
          type={type}
          large={large}
          clearable={clearable}
          ariaLabel={ariaLabel}
        />
        <AutoCompleteListBox
          isOpen={isOpen}
          onOptionSelection={optionSelectHandler}
          isLoading={isLoading}
          listBoxID={listBoxID}
          ref={listBoxRef}
          options={{ options }}
          style={style}
          dataTest={dataTest ? `${dataTest}-listbox` : listBoxID}
        />
      </div>
    )
  }
)

export default AutoComplete
